import { useEffect, useState, type ReactNode } from "react";
import type { RotuloDoEstado } from "../lib/mapaBrasil";

// O MAPA DO BRASIL, genérico — a peça que desenha os 27 contornos, e nada mais.
//
// ===========================================================================
// Por que este arquivo nasceu (27/08/2026)
// ===========================================================================
// O mapa existia dentro de `LegalizaUi.tsx`, amarrado ao que aquela tela precisa:
// pintar cada estado pela cor do INTEGRADOR (quatro valores categóricos). O Mapa
// de Concursos precisa do mesmo desenho com uma escala QUANTITATIVA — o estado
// pintado pela quantidade de inscrições abertas nele.
//
// Copiar o componente traria junto três coisas que foram PAGAS com medição, e
// que passariam a existir em dois lugares:
//
//   1. a tabela `CHAMADAS` — os oito estados cuja sigla não cabe dentro do
//      próprio contorno. No app de origem do Legaliza os cinco do Nordeste (RN,
//      PB, PE, AL, SE) saem empilhados e ilegíveis, e o DF é um ponto dentro de
//      Goiás com duas letras por cima. A saída é a do mapa impresso: a sigla vai
//      para uma coluna à margem, com uma linha de chamada;
//   2. o `import()` da geometria. Importada de forma estática, ela caía no bundle
//      principal — 131 kB de contorno de estado no primeiro carregamento de quem
//      só quer ver as aulas;
//   3. o `aria-hidden` com a razão escrita: a LISTA de estados é a navegação
//      (teclado, leitor de tela, celular — onde tocar em Sergipe é impossível de
//      qualquer jeito), e deixar o mapa também na árvore faria o leitor anunciar
//      os 27 estados duas vezes, a segunda sem endereço.
//
// Então o desenho virou esta peça e `LegalizaUi.MapaDoBrasil` passou a ser um
// invólucro dela. É a mesma relação que a `Cabine` tem com o `HeroModulo` do HUB
// DP: a forma genérica mora num lugar, e quem tem decisão própria a embrulha.
//
// O DOM que sai daqui é o MESMO de antes — `data-mapa="brasil"`, `data-uf` em
// cada path, os `<line>` das chamadas, `aria-hidden` no `<svg>` —, porque
// `app/tests/legaliza.mjs` mede exatamente esses seletores: 27 contornos, 8
// linhas de chamada, e o contraste de `[data-mapa] path[data-uf="XX"]` contra
// `.painel-navy`.

/**
 * Os oito estados cuja sigla não cabe dentro do próprio contorno.
 *
 * O `y` de cada um foi escolhido perto do `y` real do estado, para a linha sair
 * quase horizontal — chamada que cruza meio mapa liga o rótulo ao lugar errado
 * na leitura rápida.
 */
const CHAMADAS: Record<string, { y: number }> = {
  RN: { y: 150 },
  PB: { y: 182 },
  PE: { y: 214 },
  AL: { y: 246 },
  SE: { y: 278 },
  DF: { y: 330 },
  ES: { y: 392 },
  RJ: { y: 440 },
};
const COLUNA_X = 646;

type Geometria = {
  MAPA_VIEWBOX: string;
  MAPA_CONTORNOS: Record<string, string>;
  MAPA_ROTULOS: Record<string, RotuloDoEstado>;
};

/**
 * O mapa do Brasil, com a cor e o rótulo de cada estado vindos de fora.
 *
 * `corDaUf` devolve um valor de `fill` do SVG — cor literal ou `var(--cf-…)`. Um
 * token semântico entra como `var()`, e é assim que o Calendário e o Legaliza
 * resolvem contraste nos dois temas: quem pinta é o TERRITÓRIO, redefinindo a
 * variável dentro de `painel-navy`.
 *
 * `ufs` diz quais desenhar. O Legaliza passa os 27 que tem no banco; o Mapa de
 * Concursos passa os 27 sempre, porque estado SEM concurso é informação — três
 * deles (AC, GO, PA) estavam sem nenhuma vaga em 27/08/2026, e apagá-los do mapa
 * faria o Brasil parecer com um buraco em vez de com um estado sem vaga.
 */
export function MapaDoBrasilSvg({
  ufs,
  corDaUf,
  tituloDaUf,
  ativo,
  onEscolher,
  corDoTraco = "#0c1f40",
  corDoRotulo = "#ffffff",
}: {
  ufs: string[];
  corDaUf: (uf: string) => string;
  tituloDaUf: (uf: string) => string;
  /**
   * O que está selecionado. Aceita uma LISTA porque nem toda escolha é de um
   * estado só: no ForoFácil, escolher o TRF-4 acende o Paraná, Santa Catarina
   * e o Rio Grande do Sul juntos — a seleção é a jurisdição inteira.
   */
  ativo?: string | string[] | null;
  onEscolher?: (uf: string) => void;
  corDoTraco?: string;
  corDoRotulo?: string;
}) {
  // A geometria entra por `import()`, e isso foi MEDIDO: importada de forma
  // estática, ela caía no bundle principal. Não há tela de espera dedicada —
  // quando este componente monta, o catálogo já veio do banco, e o `import()` de
  // um arquivo local resolve antes de o olho notar. O que existe é a moldura de
  // quem chama reservando a altura, para a página não saltar.
  const [geo, setGeo] = useState<Geometria | null>(null);
  useEffect(() => {
    let vivo = true;
    void import("../lib/mapaBrasil").then((m) => {
      if (vivo) setGeo(m);
    });
    return () => {
      vivo = false;
    };
  }, []);

  if (!geo) return <div className="h-full w-full" aria-hidden="true" />;
  const { MAPA_VIEWBOX, MAPA_CONTORNOS, MAPA_ROTULOS } = geo;
  const presentes = new Set(ufs);
  const selecionados = new Set(ativo == null ? [] : Array.isArray(ativo) ? ativo : [ativo]);
  const haSelecao = selecionados.size > 0;

  return (
    <svg
      viewBox={MAPA_VIEWBOX}
      preserveAspectRatio="xMidYMid meet"
      // A altura sai da RAZÃO do desenho, não de uma altura mínima no container:
      // com `min-h` + `h-full` o SVG centralizava dentro de uma caixa maior que
      // ele e sobrava navy vazio embaixo.
      className="h-auto w-full"
      // O escopo NOMEADO que a prova usa. Sem ele, `document.querySelectorAll
      // ('svg path')` conta também os ícones do Topo e do Rodapé: a primeira
      // rodada do Legaliza mediu 33 contornos onde há 27.
      data-mapa="brasil"
      // `aria-hidden`, e a razão é que existe um caminho MELHOR ao lado: a lista
      // de estados é feita de elementos focáveis de verdade, e ela é o caminho do
      // teclado, do leitor de tela e do celular. O mapa é o atalho visual; a
      // lista é a navegação.
      aria-hidden="true"
      focusable="false"
    >
      {Object.entries(MAPA_CONTORNOS).map(([uf, contorno]) => {
        if (!presentes.has(uf)) return null;
        const rotulo = MAPA_ROTULOS[uf];
        const chamada = CHAMADAS[uf];
        const selecionado = selecionados.has(uf);

        return (
          <g
            key={uf}
            className={onEscolher ? "cursor-pointer" : undefined}
            onClick={onEscolher ? () => onEscolher(uf) : undefined}
            style={{ transition: "opacity .15s ease" }}
          >
            <title>{tituloDaUf(uf)}</title>
            <path
              data-uf={uf}
              d={contorno}
              fill={corDaUf(uf)}
              stroke={corDoTraco}
              strokeWidth={selecionado ? 3 : 1.5}
              vectorEffect="non-scaling-stroke"
              opacity={haSelecao && !selecionado ? 0.45 : 1}
            />
            {chamada ? (
              <>
                {/* O ponto no território e a linha até a margem. */}
                <circle cx={rotulo.x} cy={rotulo.y} r={3} fill={corDoRotulo} opacity={0.9} />
                <line
                  x1={rotulo.x + 4}
                  y1={rotulo.y}
                  x2={COLUNA_X - 6}
                  y2={chamada.y}
                  stroke={corDoRotulo}
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                  opacity={0.45}
                />
                <text
                  x={COLUNA_X}
                  y={chamada.y}
                  textAnchor="start"
                  dominantBaseline="middle"
                  fill={corDoRotulo}
                  fontSize="15"
                  fontWeight="800"
                  className="pointer-events-none select-none"
                >
                  {uf}
                </text>
              </>
            ) : (
              <text
                x={rotulo.x}
                y={rotulo.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={corDoRotulo}
                fontSize="14"
                fontWeight="800"
                className="pointer-events-none select-none"
              >
                {uf}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ===========================================================================
// A ESCALA do coroplético
// ===========================================================================
// Cinco degraus, e o primeiro é "nenhum". A razão de o zero ter degrau próprio:
// num mapa pintado por quantidade, estado sem nada e estado com pouco ficam
// parecidos, e a pergunta que o aluno faz ao abrir o mapa é justamente "tem vaga
// no meu estado?".
//
// Os cortes saem da MEDIÇÃO, não de uma escala redonda. Em 27/08/2026 as 32
// inscrições abertas estavam assim: PR com 6, RS com 6, MG com 5, SP com 5, RJ
// com 2, e MS, PE, PI, RR e BA com 1 cada — 17 estados com zero. Uma escala
// linear de 0 a 6 daria cinco tons quase iguais; estes cortes separam "nenhum",
// "um", "dois a três", "quatro a cinco" e "seis ou mais", que é a forma real do
// dado.
//
// E as cores são TOKEN semântico, não cor de marca. Medido em 25/08 ao desenhar
// as três cores de setor do Calendário: `bg-azul` dá 14,57 : 1 no tema claro e
// **1,07** no escuro; `bg-laranja` dá 7,33 no escuro e **2,13** no claro. Não
// existe cor fixa da marca que passe 3 : 1 (o mínimo de forma não textual) nos
// dois fundos. Como este mapa mora dentro de `painel-navy`, que é escuro nos
// DOIS temas, é o território que redefine as variáveis — e nenhum componente
// daqui precisa saber onde está.
export type DegrauDoMapa = { ate: number; token: string; rotulo: string };

export const ESCALA_DE_ABERTAS: DegrauDoMapa[] = [
  { ate: 0, token: "var(--cf-mapa-0)", rotulo: "nenhuma" },
  { ate: 1, token: "var(--cf-mapa-1)", rotulo: "1" },
  { ate: 3, token: "var(--cf-mapa-2)", rotulo: "2 a 3" },
  { ate: 5, token: "var(--cf-mapa-3)", rotulo: "4 a 5" },
  { ate: Infinity, token: "var(--cf-mapa-4)", rotulo: "6 ou mais" },
];

export function corDoDegrau(n: number, escala = ESCALA_DE_ABERTAS): string {
  for (const d of escala) if (n <= d.ate) return d.token;
  return escala[escala.length - 1].token;
}

/**
 * A legenda da escala.
 *
 * Ela existe porque mapa pintado por quantidade sem legenda é decoração: o olho
 * vê que um estado é mais escuro que o outro e não sabe quanto isso vale. E o
 * texto carrega o número, não só a cor — cor sozinha não informa quem não
 * distingue os tons.
 */
export function LegendaDaEscala({
  escala = ESCALA_DE_ABERTAS,
  titulo,
}: {
  escala?: DegrauDoMapa[];
  titulo: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
      <span className="font-semibold uppercase tracking-[0.12em] text-navy-mute">{titulo}</span>
      {escala.map((d) => (
        <span key={d.rotulo} className="inline-flex items-center gap-1.5 text-navy-ink">
          <span
            aria-hidden="true"
            className="inline-block size-3 rounded-[3px] ring-1 ring-white/25"
            style={{ backgroundColor: d.token }}
          />
          {d.rotulo}
        </span>
      ))}
    </div>
  );
}
